<template>
  <div class="sp-switch" :class="classModifiers">
    <div class="sp-switch__selection-control">
      <div class="sp-switch__control-wrapper" @click="toggleChecked">
        <div class="sp-switch__control-input">
          <input
            id="switch"
            ref="switchInput"
            type="checkbox"
            :checked="checked"
            :disabled="disabled"
            :aria-labelledby="labelledBy"
            :aria-label="labelText"
            :aria-describedby="describedBy"
            @click.stop
            @input.stop="onInnerInput"
            @change="onUserToggle"
            @focus="onFocus"
            @blur="onBlur"
          />
          <div class="sp-switch__track">
            <div class="sp-switch__thumb"></div>
          </div>
        </div>
      </div>
      <sp-label :id="LABEL_ID" class="sp-switch__label" @click="toggleChecked">{{ label }}</sp-label>
    </div>
    <div v-if="shownMessages.length > 0" :id="MESSAGES_ID" class="sp-switch__messages" part="messages">
      <span v-for="(message, index) in shownMessages" :key="index" class="sp-switch__message">{{ message }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, toRef, useTemplateRef } from "vue";
import { toSystemColorVariable } from "#ce/utils/color";
import { useCheckedModel } from "#ce/composables/useCheckedModel";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { useFormControl } from "#ce/composables/useFormControl";
import { isPresent, toArray, toBoolean } from "#ce/utils/props";
import { props as switchProps } from "./props.js";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-switch is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(formAssociatedHost());

const emit = defineEmits(["input", "update-checked"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(switchProps);

const { checked, defaultChecked, setChecked, reset } = useCheckedModel();
const switchInput = useTemplateRef("switchInput");

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// The visible control and label sit outside the native input's disabled/click
// handling. Guard those clicks, then route through the checkbox so input:checked
// remains the visual source of truth.
function toggleChecked() {
  if (disabled.value) {
    return;
  }

  // Focused first, the way a native `<label for>` activation does, so anything keyed to focus —
  // the hint — reacts to a label click as it does to the control's own.
  switchInput.value?.focus();
  switchInput.value?.click();
}

const { exposeProperties } = useExpose();

exposeProperties({
  type: {
    configurable: true,
    get: () => "checkbox",
  },
  checked: {
    configurable: true,
    get: () => checked.value,
    set: setChecked,
  },
  defaultChecked: {
    configurable: true,
    get: () => defaultChecked.value,
  },
});

const LABEL_ID = "sp-switch-label";
const MESSAGES_ID = "sp-switch-messages";

const isFocused = ref(false);

function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
}

// An aria-labelledby pointing at a label with no text names the control with an empty string,
// which is worse than no reference at all. The accessibility tree trims, so whitespace is no text.
const labelledBy = computed(() => (props.label?.trim() ? LABEL_ID : undefined));

// The visible label wins: naming the control twice leaves the two to fight in the accessibility
// tree, and `aria-labelledby` is the one that would win anyway.
const labelText = computed(() => (labelledBy.value ? undefined : props.ariaLabel));

// A message displaces the hint, which otherwise shows only while the switch has focus. An empty
// `messages` says nothing, so the hint still gets its turn. The area is absent when neither has
// anything to say, so the switch reserves no space below it.
const shownMessages = computed(() => {
  const messages = toArray(props.messages ?? [])
    .filter(Boolean)
    .map(String);

  if (messages.length > 0) {
    return messages;
  }

  if (props.hint && (toBoolean(props.persistentHint) || isFocused.value)) {
    return [props.hint];
  }

  return [];
});

const describedBy = computed(() => (shownMessages.value.length > 0 ? MESSAGES_ID : undefined));

const trackCheckedColor = computed(() => toSystemColorVariable(props.color));

const classModifiers = computed(() => ({
  "--checked": checked.value,
  "--disabled": disabled.value,
  "--label-prepend": toBoolean(props.labelPrepend),
}));

// The inner input sits in the shadow root, where the outer form cannot see it, so the element
// submits through ElementInternals: the value under the host's own name while the switch is on,
// and nothing while it is off, the way a native checkbox behaves.
const { emitChange, mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();
const formValue = computed(() => (checked.value ? props.value : null));

mirrorFormValue(formValue);

// The inner input's own `input` is stopped and re-reported after the model is set, so
// `host.checked` is already current for any listener the event reaches. A v-model binding reads
// it inside the host's `change`, which does not compose out of the shadow root — the host
// dispatches its own. This is the only path that emits `update-checked`: a native control
// reports user input alone, and property writes, attribute syncs, and resets mirror silently.
function onInnerInput(event) {
  setChecked(event.target.checked);
  emitBubbling("input", checked.value);
  emit("update-checked", checked.value);
}

// A native control reports the user's toggle and stays silent for a scripted one, including a
// reset, so the form value is kept in sync silently and the toggle reports itself.
function onUserToggle() {
  emitChange();
}

onFormReset(reset);

// A required switch is satisfied only while it is on, the way a required checkbox is.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("switch_value_missing"));

mirrorValueMissing(() => isRequired.value && !checked.value, validationMessage, switchInput);
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-switch {
  --track-width: var(--sp-comp-switch-track-min-width, 36px);
  --track-bg-color-checked: var(--sp-comp-switch-track-bg-color-checked, v-bind(trackCheckedColor));
  --selection-control-columns: auto 1fr;
  --selection-control-areas: "control label";

  display: grid;
  position: relative;
  grid-template-areas: "control" "message";
  grid-template-rows: auto auto;

  &.--disabled {
    .sp-switch__track,
    label {
      opacity: 0.3;
    }
  }
  &.--label-prepend {
    --selection-control-columns: 1fr auto;
    --selection-control-areas: "label control";
  }
}

.sp-switch__selection-control {
  display: grid;
  grid-template-columns: var(--selection-control-columns);
  grid-template-areas: var(--selection-control-areas);
  gap: var(--sp-com-switch-label-gap, 1ch);
}

.sp-switch__control-wrapper {
  display: flex;
  min-width: var(--sp-comp-switch-control-wrapper-min-width, var(--track-width));
  align-items: center;
  position: relative;
  justify-content: center;
  flex: none;
  grid-area: control;
}

.sp-switch__track {
  --track-bg-color: var(--sp-comp-switch-track-background-color, #afafaf);
  --track-height: var(--sp-comp-switch-track-height, 16px);
  --track-outline-width: var(--sp-comp-switch-track-outline-width, 1px);

  min-width: var(--track-width);
  height: var(--track-height);
  background-color: var(--track-bg-color);
  border-radius: calc(var(--track-height) / 2);
  outline: var(--track-outline-width) solid var(--sp-comp-track-outline-color, var(--track-bg-color));
  position: absolute;
  margin-left: var(--track-outline-width);
}

.sp-switch .sp-switch__control-input {
  border-radius: 50%;
  position: absolute;
}

.sp-switch__control-input {
  min-width: var(--sp-comp-switch-control-input-min-width, var(--track-width));
  position: absolute;
  height: 50%;
  align-items: center;
  display: flex;
  flex: none;
  justify-content: center;
}

.sp-switch__thumb {
  --thumb-size: var(--sp-comp-switch-thumb-size, var(--track-height));

  background-color: var(--sp-comp-switch-thumb-background-color, #fff);
  border-radius: calc(var(--thumb-size) / 2);
  color: var(--sp-comp-switch-thumb-color, #000);
  display: flex;
  height: var(--thumb-size);
  width: var(--thumb-size);
  justify-content: center;
  pointer-events: none;
  position: absolute;
  left: 0;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  transform: translateX(0);
}

.sp-switch__label {
  grid-area: label;
}

input {
  cursor: pointer;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  margin: 0;
  padding: 0;
  z-index: 3;
}

input:checked + .sp-switch__track .sp-switch__thumb {
  transform: translateX(calc(var(--track-width) - var(--thumb-size)));
}

input:checked + .sp-switch__track {
  --track-bg-color: var(--track-bg-color-checked);
}

input:disabled {
  cursor: not-allowed;
}

input:disabled + .sp-switch__track {
  background-color: var(--track-bg-color);
}

#switch {
  opacity: 0;
  border-radius: 50%;
}

.sp-switch__messages {
  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-switch-message-gap, var(--sp-sys-form-field-message-gap));
  margin-top: var(--sp-comp-switch-message-margin-top, var(--sp-sys-form-field-message-margin-top));
  color: var(--sp-comp-switch-message-color, var(--sp-sys-form-field-message-color));
  font-size: var(--sp-comp-switch-message-font-size, var(--sp-sys-form-field-message-font-size));
  line-height: var(--sp-comp-switch-message-line-height, var(--sp-sys-form-field-message-line-height));
}
</style>
